/**
 * Game Constants - Central configuration for Sticky Planet
 * 
 * All magic numbers and tunable values are defined here for easy balancing.
 * Values are optimized for mobile-first hyper-casual gameplay.
 */

// ============================================================
// GAME IDENTITY
// ============================================================
export const GAME_NAME = 'Sticky Planet';
export const GAME_VERSION = '1.0.0';

// ============================================================
// DISPLAY SETTINGS
// ============================================================
export const GAME_WIDTH = 720;   // Base width (scales to device)
export const GAME_HEIGHT = 1280; // Base height (portrait mode)
export const TARGET_FPS = 60;    // Target frame rate

// ============================================================
// PHYSICS CONSTANTS
// ============================================================
export const PHYSICS = {
  // Gravity strength pulling player toward planet center
  GRAVITY_STRENGTH: 400, // Increased for snappier feel
  
  // Maximum velocity the player can reach
  MAX_VELOCITY: 1400, // Slightly reduced for stability
  
  // Launch power multiplier (higher = faster launch)
  LAUNCH_POWER: 1350,
  
  // Minimum launch power to prevent weak launches
  MIN_LAUNCH_POWER: 850,
  
  // Maximum launch power cap
  MAX_LAUNCH_POWER: 1700,
  
  // Drag applied when in space (0 = no drag, 1 = full drag)
  SPACE_DRAG: 0.998, // Slightly more drag for control
  
  // Rotation speed when orbiting planet (radians/sec)
  ORBIT_SPEED: 2.8, // Faster orbiting
  
  // Distance from planet center to surface for "sticky" landing
  STICK_THRESHOLD: 25, // Tighter landing detection
  
  // Time window to chain combos (ms)
  COMBO_WINDOW: 2500, // Extended for more combos
  
  // Immunity time after launch (ms) - prevents instant re-landing
  LAUNCH_IMMUNITY_TIME: 350,
  
  // Minimum escape distance before gravity re-applies
  MIN_ESCAPE_DISTANCE: 70,
  
  // Delta time clamping (prevents physics explosion on lag)
  MAX_DELTA: 0.05, // 20 FPS minimum
  
  // Double-tap prevention cooldown (ms)
  TAP_COOLDOWN: 100
} as const;

// ============================================================
// PLANET CONFIGURATION
// ============================================================
export const PLANET = {
  // Minimum radius for planets
  MIN_RADIUS: 40,
  
  // Maximum radius for planets
  MAX_RADIUS: 100,
  
  // Minimum distance between planet centers
  MIN_DISTANCE: 200,
  
  // Maximum distance between planet centers
  MAX_DISTANCE: 400,
  
  // Gravity field multiplier (how far gravity reaches)
  GRAVITY_FIELD_MULTIPLIER: 3.0,
  
  // Number of planets to keep in pool (INCREASED for infinite generation)
  POOL_SIZE: 20,
  
  // Colors for planets (vibrant gradient theme)
  COLORS: [
    0x6366f1, // Indigo
    0x8b5cf6, // Violet
    0xec4899, // Pink
    0xf43f5e, // Rose
    0xf97316, // Orange
    0x14b8a6, // Teal
    0x22c55e, // Green
    0x3b82f6  // Blue
  ] as const
} as const;

// ============================================================
// PLAYER CONFIGURATION
// ============================================================
export const PLAYER = {
  // Player ball radius
  RADIUS: 15,
  
  // Trail length (number of points)
  TRAIL_LENGTH: 15,
  
  // Trail fade speed
  TRAIL_FADE: 0.95,
  
  // Default color
  COLOR: 0x00d4ff,
  
  // Shield color
  SHIELD_COLOR: 0x00ff88,
  
  // Death animation duration (ms)
  DEATH_DURATION: 500,
  
  // Launch hold time for max power (ms)
  MAX_HOLD_TIME: 1000
} as const;

// ============================================================
// DIFFICULTY PROGRESSION
// ============================================================
export const DIFFICULTY = {
  // Starting distance between planets
  INITIAL_DISTANCE: 180,
  
  // Maximum distance at highest difficulty
  MAX_DISTANCE: 350,
  
  // Score needed to reach max difficulty
  MAX_DIFFICULTY_SCORE: 50,
  
  // Curve exponent (higher = steeper ramp)
  CURVE_EXPONENT: 0.7,
  
  // Planet speed increase per level
  SPEED_INCREASE: 0.05
} as const;

// ============================================================
// POWER-UPS
// ============================================================
export const POWERUPS = {
  // Spawn chance per planet (0-1)
  SPAWN_CHANCE: 0.15,
  
  // Duration of power-up effects (ms)
  DURATION: 5000,
  
  // Types of power-ups
  TYPES: ['shield', 'slowmo', 'magnet'] as const,
  
  // Slow-mo time scale
  SLOWMO_SCALE: 0.5,
  
  // Magnet pull strength
  MAGNET_STRENGTH: 200
} as const;

// ============================================================
// SCORING
// ============================================================
export const SCORING = {
  // Base points per successful landing
  BASE_POINTS: 10,
  
  // Combo multiplier cap
  MAX_COMBO: 10,
  
  // Points per combo level
  COMBO_BONUS: 5,
  
  // Perfect landing bonus (landing quickly)
  PERFECT_BONUS: 20,
  
  // Perfect landing time window (ms)
  PERFECT_WINDOW: 500
} as const;

// ============================================================
// AUDIO
// ============================================================
export const AUDIO = {
  // Default volume levels (0-1)
  MUSIC_VOLUME: 0.3,
  SFX_VOLUME: 0.6,
  
  // Audio keys
  KEYS: {
    MUSIC_MAIN: 'music_main',
    SFX_LAUNCH: 'sfx_launch',
    SFX_LAND: 'sfx_land',
    SFX_DEATH: 'sfx_death',
    SFX_POWERUP: 'sfx_powerup',
    SFX_COMBO: 'sfx_combo',
    SFX_UI: 'sfx_ui'
  } as const
} as const;

// ============================================================
// UI CONSTANTS
// ============================================================
export const UI = {
  // Font family for all text
  FONT_FAMILY: '"Segoe UI", system-ui, sans-serif',
  
  // Colors
  PRIMARY_COLOR: '#00d4ff',
  SECONDARY_COLOR: '#00ff88',
  BACKGROUND_COLOR: '#0a0a1a',
  TEXT_COLOR: '#ffffff',
  
  // Animation durations (ms)
  FADE_DURATION: 300,
  SLIDE_DURATION: 400,
  
  // Button sizes
  BUTTON_HEIGHT: 60,
  BUTTON_PADDING: 20
} as const;

// ============================================================
// PARTICLE EFFECTS
// ============================================================
export const PARTICLES = {
  // Launch burst particle count
  LAUNCH_COUNT: 20,
  
  // Landing particle count
  LAND_COUNT: 15,
  
  // Death explosion particle count
  DEATH_COUNT: 30,
  
  // Trail particle lifetime (ms)
  TRAIL_LIFETIME: 300,
  
  // Particle pool size
  POOL_SIZE: 100
} as const;

// ============================================================
// PERFORMANCE BUDGETS
// ============================================================
export const PERFORMANCE = {
  // Maximum memory usage (MB)
  MAX_MEMORY_MB: 150,
  
  // Target load time (ms)
  TARGET_LOAD_TIME: 5000,
  
  // Maximum bundle size (MB)
  MAX_BUNDLE_SIZE_MB: 25,
  
  // Object pool preallocation counts
  PREALLOC_PLANETS: 20,
  PREALLOC_PARTICLES: 100,
  PREALLOC_POWERUPS: 5
} as const;

// ============================================================
// PLATFORM DETECTION
// ============================================================
export const PLATFORMS = {
  CRAZYGAMES: 'crazygames',
  POKI: 'poki',
  GAMEMONETIZE: 'gamemonetize',
  WEB: 'web'
} as const;

// ============================================================
// SEEDED RANDOM - For reproducible procedural generation
// ============================================================
export const DEFAULT_SEED = 12345;

// ============================================================
// SCENE KEYS
// ============================================================
export const SCENES = {
  BOOT: 'BootScene',
  PRELOAD: 'PreloadScene',
  MENU: 'MenuScene',
  GAME: 'GameScene',
  GAME_OVER: 'GameOverScene'
} as const;

// ============================================================
// STORAGE KEYS (LocalStorage)
// ============================================================
export const STORAGE = {
  HIGH_SCORE: 'sticky_planet_high_score',
  SOUND_ENABLED: 'sticky_planet_sound',
  MUSIC_ENABLED: 'sticky_planet_music',
  COLORBLIND_MODE: 'sticky_planet_colorblind',
  GAMES_PLAYED: 'sticky_planet_games_played'
} as const;
